import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { useParams, useNavigate } from 'react-router-dom';
import { GET_MEETINGS_MINIMAL } from '@shared/graphql/queries';
import { MainLayout } from '../layouts';
import { MeetingCard, Button } from '../components';
import { Meeting } from '../types';
import { parseDateFromPath, formatDatePath, formatDateDisplay, formatDateForApi, getTodayAtNoon, getYesterdayAtNoon, getTomorrowAtNoon } from '../utils';

const MeetingsPage: React.FC = () => {
  const { dateString } = useParams<{ dateString: string }>();
  const navigate = useNavigate();
  
  // Parse date from URL or use today
  const initialDate = dateString ? parseDateFromPath(dateString) : getTodayAtNoon();
  const [selectedDate, setSelectedDate] = useState<Date>(initialDate);
  
  // Update URL when date changes
  useEffect(() => {
    if (dateString !== formatDatePath(selectedDate)) {
      navigate(`/meetings/${formatDatePath(selectedDate)}`);
    }
  }, [selectedDate, dateString, navigate]);
  
  // Query meetings for the selected date
  const { loading, error, data } = useQuery(GET_MEETINGS_MINIMAL, {
    variables: { date: formatDateForApi(selectedDate) },
  });
  
  // Group meetings by state
  const groupMeetingsByState = (meetings: Meeting[]) => {
    const grouped: Record<string, Meeting[]> = {};
    
    meetings?.forEach((meeting) => {
      const state = meeting.track.state;
      if (!grouped[state]) {
        grouped[state] = [];
      }
      grouped[state].push(meeting);
    });
    
    return grouped;
  };
  
  const groupedMeetings = groupMeetingsByState(data?.meetings || []);
  
  // Handle date navigation
  const handlePreviousDay = () => {
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    setSelectedDate(prevDay);
  };
  
  const handleNextDay = () => {
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    setSelectedDate(nextDay);
  };
  
  const handleToday = () => {
    setSelectedDate(getTodayAtNoon());
  };
  
  return (
    <MainLayout>
      <div className="space-y-8">
        {/* Page header */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Meetings</h1>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handlePreviousDay}>
              Previous Day
            </Button>
            <Button variant="primary" onClick={handleToday}>
              Today
            </Button>
            <Button variant="outline" onClick={handleNextDay}>
              Next Day
            </Button>
          </div>
        </div>
        
        {/* Date display */}
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold text-center">
            {formatDateDisplay(selectedDate)}
          </h2>
        </div>
        
        {/* Meetings list */}
        <div className="space-y-8">
          {loading ? (
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <p className="text-gray-500">Loading meetings...</p>
            </div>
          ) : error ? (
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <p className="text-red-500">Error loading meetings: {error.message}</p>
            </div>
          ) : data?.meetings?.length > 0 ? (
            <>
              {Object.entries(groupedMeetings).map(([state, meetings]) => (
                <section key={state}>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">{state}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {meetings.map(meeting => (
                      <MeetingCard key={meeting.meeting_id} meeting={meeting} />
                    ))}
                  </div>
                </section>
              ))}
              
              {/* ROI Explanation Footer */}
              {data?.meetings?.some((meeting: Meeting) => meeting.performanceImproved) && (
                <div className="mt-8 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center">
                    * ROI (Return on Investment) represents the percentage return if you placed $1 on every race at the meeting using our top picks.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <p className="text-gray-500">No meetings available for this date.</p>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default MeetingsPage;
